import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { useApi, useConsulta } from '@plataforma/web/api';
import { fechaCorta, mxn } from '../formato';

interface Plan {
  clave: string;
  nombre: string;
  descripcion: string | null;
  diasPrueba: number;
  modulos: { clave: string; nombre: string; descripcion: string }[];
  precios: { periodicidad: 'mensual' | 'anual'; montoMxn: string; conIvaMxn: string }[];
}
interface Suscripcion {
  estado: string;
  vigente: boolean;
  plan: { clave: string; nombre: string; modulos: string[] };
  periodicidad: 'mensual' | 'anual';
  precio: { montoMxn: string; conIvaMxn: string } | null;
  proveedor: string;
  diasPrueba: number;
  periodoFin: string | null;
  graciaHasta: string | null;
  cancelarAlFinal: boolean;
  pagoEnLinea: boolean;
}
/** La IA del mes del lotero (arquitectura 2.22). */
interface IaDelMes {
  mes: string;
  incluidoUsd: string;
  usadoUsd: string;
  porcentaje: number | null;
  aviso: 80 | 100 | null;
  autos: { revisados: number; costoPromedioUsd: string; restantes: number | null };
  excedente: { costoUsd: string; margenPorcentaje: string; cobroUsd: string; cobroMxn: string };
}
const usd = (v: string) =>
  `US$${Number(v).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const nombreMes = (mes: string) =>
  new Intl.DateTimeFormat('es-MX', { month: 'long', timeZone: 'UTC' }).format(
    new Date(`${mes}-15T12:00:00Z`),
  );

/**
 * IA del mes: dólares de IA incluidos en el plan contra lo usado, cuántos autos más alcanzan y el
 * excedente que irá a la siguiente factura (a costo + el margen del catálogo).
 */
function IaDelMesSeccion({ ia }: { ia: IaDelMes }) {
  const sinIncluido = Number(ia.incluidoUsd) === 0;
  if (sinIncluido && Number(ia.usadoUsd) === 0) return null;
  const pct = ia.porcentaje ?? 0;
  const hayExcedente = Number(ia.excedente.costoUsd) > 0;
  return (
    <section className="seccion" aria-labelledby="ia-del-mes">
      <header>
        <h2 id="ia-del-mes">IA de {nombreMes(ia.mes)}</h2>
      </header>
      <div className="cuerpo">
        {!sinIncluido && (
          <>
            <meter
              min={0}
              max={100}
              low={80}
              high={99}
              optimum={0}
              value={Math.min(pct, 100)}
              aria-label="Uso de la IA del mes"
              style={{ width: '100%', height: 14 }}
            />
            <p className="nota">
              {pct} % usado · {usd(ia.usadoUsd)} de {usd(ia.incluidoUsd)} incluidos
            </p>
          </>
        )}
        {ia.aviso === 80 && (
          <p className="etiqueta aviso">Llevas más del 80 % de tu IA de este mes.</p>
        )}
        {ia.aviso === 100 && (
          <p className="etiqueta aviso">
            Ya usaste toda tu IA incluida; la IA sigue funcionando y lo de más se cobra aparte.
          </p>
        )}
        <div className="tarjetas">
          <div className="tarjeta">
            <div className="rotulo">Autos revisados</div>
            <div className="cifra">{ia.autos.revisados}</div>
            <div className="nota">promedio {usd(ia.autos.costoPromedioUsd)} por auto</div>
          </div>
          {!sinIncluido && (
            <div className="tarjeta">
              <div className="rotulo">Te alcanza para</div>
              <div className="cifra">{ia.autos.restantes ?? '—'}</div>
              <div className="nota">autos más este mes, a tu costo promedio</div>
            </div>
          )}
          <div className="tarjeta">
            <div className="rotulo">Excedente</div>
            <div className="cifra">{hayExcedente ? mxn(ia.excedente.cobroMxn) : '—'}</div>
            <div className="nota">
              {hayExcedente
                ? `${usd(ia.excedente.costoUsd)} a costo + ${ia.excedente.margenPorcentaje} %, más IVA, en tu siguiente factura`
                : `Lo que pase de lo incluido se cobra a costo + ${ia.excedente.margenPorcentaje} %`}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const ESTADO_SUSCRIPCION: Record<string, string> = {
  prueba: 'En prueba',
  activa: 'Activa',
  pago_pendiente: 'Pago pendiente',
  vencida: 'Vencida',
  cancelada: 'Cancelada',
};

/** Mi suscripción: plan actual, cambiar de plan, pagar con tarjeta y cancelar. */
export function MiSuscripcion() {
  const api = useApi();
  const [params] = useSearchParams();
  const actual = useConsulta<Suscripcion>('/platform/mi-lotero/suscripcion');
  const planes = useConsulta<Plan[]>('/platform/planes');
  const ia = useConsulta<IaDelMes>('/platform/mi-lotero/suscripcion/ia');
  const [periodicidad, setPeriodicidad] = useState<'mensual' | 'anual'>('mensual');
  const [error, setError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const s = actual.datos;

  const hacer = async (fn: () => Promise<unknown>) => {
    setError(null);
    setOcupado(true);
    try {
      await fn();
      actual.recargar();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setOcupado(false);
    }
  };
  const pagar = () =>
    hacer(async () => {
      const { url } = await api<{ url: string }>('POST', '/platform/mi-lotero/suscripcion/pago');
      window.location.assign(url);
    });

  return (
    <>
      <div className="encabezado">
        <div>
          <h1>Mi suscripción</h1>
          <div className="subtitulo">Tu plan, sus módulos y el cobro</div>
        </div>
      </div>
      {params.get('pago') === 'listo' && (
        <p className="etiqueta exito">Pago recibido: tu suscripción se activa en unos segundos.</p>
      )}
      {params.get('pago') === 'cancelado' && <p className="error">El pago no se completó.</p>}
      {error && <p className="error">{error}</p>}
      {s && (
        <div className="tarjetas">
          <div className="tarjeta">
            <div className="rotulo">Plan</div>
            <div className="cifra">{s.plan.nombre}</div>
            <div className="nota">
              {ESTADO_SUSCRIPCION[s.estado] ?? s.estado}
              {s.estado === 'prueba' ? ` · quedan ${s.diasPrueba} días` : ''}
            </div>
          </div>
          <div className="tarjeta">
            <div className="rotulo">Precio</div>
            <div className="cifra">{s.precio ? mxn(s.precio.conIvaMxn) : '—'}</div>
            <div className="nota">{s.periodicidad} con IVA</div>
          </div>
          <div className="tarjeta">
            <div className="rotulo">Periodo</div>
            <div className="cifra">{fechaCorta(s.periodoFin)}</div>
            <div className="nota">
              {s.cancelarAlFinal ? 'se cancela al terminar' : 'se renueva solo'}
            </div>
          </div>
        </div>
      )}
      {s && (
        <div style={{ display: 'flex', gap: 8 }}>
          {s.pagoEnLinea && ['prueba', 'pago_pendiente', 'vencida'].includes(s.estado) && (
            <button className="boton primario" disabled={ocupado} onClick={() => void pagar()}>
              Pagar con tarjeta
            </button>
          )}
          {!s.pagoEnLinea && (
            <span className="subtitulo">
              El pago en línea no está disponible en este ambiente; Super Admin activa el plan.
            </span>
          )}
          {s.estado === 'activa' && !s.cancelarAlFinal && (
            <button
              className="boton"
              disabled={ocupado}
              onClick={() =>
                window.confirm('¿Cancelar al terminar el periodo? Tus datos se conservan.') &&
                void hacer(() => api('POST', '/platform/mi-lotero/suscripcion/cancelar'))
              }
            >
              Cancelar suscripción
            </button>
          )}
        </div>
      )}

      {ia.datos && <IaDelMesSeccion ia={ia.datos} />}

      <section className="seccion">
        <header>
          <h2>Planes</h2>
          <div style={{ flex: 1 }} />
          <select
            value={periodicidad}
            aria-label="Periodicidad"
            onChange={(e) => setPeriodicidad(e.target.value as 'mensual' | 'anual')}
          >
            <option value="mensual">Mensual</option>
            <option value="anual">Anual</option>
          </select>
        </header>
        <div className="tarjetas cuerpo">
          {(planes.datos ?? []).map((p) => {
            const precio = p.precios.find((x) => x.periodicidad === periodicidad);
            const esActual = s?.plan.clave === p.clave;
            return (
              <div key={p.clave} className="tarjeta">
                <div className="rotulo">{p.nombre}</div>
                <div className="cifra">{precio ? mxn(precio.conIvaMxn) : '—'}</div>
                <div className="nota">{p.descripcion}</div>
                <ul style={{ paddingLeft: 18, fontSize: 13 }}>
                  {p.modulos.map((m) => (
                    <li key={m.clave}>{m.nombre}</li>
                  ))}
                </ul>
                {esActual ? (
                  <span className="etiqueta exito">Tu plan</span>
                ) : (
                  <button
                    className="boton"
                    disabled={ocupado || !precio}
                    onClick={() =>
                      void hacer(() =>
                        api('POST', '/platform/mi-lotero/suscripcion', {
                          plan: p.clave,
                          periodicidad,
                        }),
                      )
                    }
                  >
                    Elegir
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </section>
    </>
  );
}

/** Mi dominio: conectar el .com propio con CNAME y TXT y verificarlo. */
